import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Sparkles, CalendarDays } from "lucide-react";
import api from "@/lib/api";

const TYPE_STYLES = {
  info: "border-gold/30 bg-coal text-ivory",
  promo: "border-copper/40 bg-copper text-ivory",
  evenement: "border-gold/40 bg-gold text-ink",
};

export default function PromoBanner() {
  const { pathname } = useLocation();
  const [banners, setBanners] = useState([]);
  const [eventMode, setEventMode] = useState(null);
  const [dismissed, setDismissed] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/banners/active").then((r) => setBanners(r.data || [])).catch(() => {});
    api
      .get("/settings")
      .then((r) => {
        const em = r.data?.event_mode;
        if (em?.enabled) setEventMode(em);
      })
      .catch(() => {});
  }, []);

  const visible = banners.filter((b) => !dismissed.includes(b.id));
  const showEvent = eventMode && pathname === "/palais";
  const hasContent = visible.length > 0 || showEvent;

  useEffect(() => {
    const setOffset = () => {
      const h = hasContent && ref.current ? ref.current.offsetHeight : 0;
      document.documentElement.style.setProperty("--banner-h", `${h}px`);
    };
    setOffset();
    window.addEventListener("resize", setOffset);
    return () => {
      window.removeEventListener("resize", setOffset);
      document.documentElement.style.setProperty("--banner-h", "0px");
    };
  }, [hasContent, visible.length, showEvent]);

  if (!hasContent) return null;

  return (
    <div ref={ref} className="fixed inset-x-0 top-0 z-[60]" data-testid="promo-banners">
      {showEvent && (
        <div className="border-b border-gold/40 bg-gold px-4 py-2.5 text-center" data-testid="event-mode-banner">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-ink sm:text-sm">
            <CalendarDays className="mr-2 inline h-4 w-4" strokeWidth={1.5} />
            {eventMode.title}
            {eventMode.subtitle && <span className="ml-2 font-normal normal-case tracking-normal">{eventMode.subtitle}</span>}
          </p>
        </div>
      )}
      {visible.map((b) => {
        const style = TYPE_STYLES[b.type] || TYPE_STYLES.info;
        const content = (
          <p className="text-xs font-semibold uppercase tracking-[0.15em] sm:text-sm">
            <Sparkles className="mr-2 inline h-3.5 w-3.5" strokeWidth={1.5} />
            {b.text}
          </p>
        );
        return (
          <div key={b.id} className={`relative border-b px-4 py-2 text-center ${style}`} data-testid={`promo-banner-${b.id}`}>
            {b.link ? (
              <Link to={b.link} className="inline-block hover:opacity-80" data-testid={`promo-banner-link-${b.id}`}>
                {content}
              </Link>
            ) : (
              content
            )}
            <button
              onClick={() => setDismissed((d) => [...d, b.id])}
              aria-label="Fermer la bannière"
              className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
              data-testid={`promo-banner-close-${b.id}`}
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
